import { MethodIdSchema } from '@dpem/ontology';
import * as z from 'zod';

import { ActorRoleSchema, MethodStateSchema, UserAccountStatusSchema } from '../common/enums.js';
import { paginated, PageQuerySchema } from '../common/pagination.js';
import {
  DescriptionSchema,
  EmailSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  ShortTextSchema,
} from '../common/primitives.js';

/**
 * access context — admin settings, including the AWS Bedrock credential.
 *
 * The credential is WRITE-ONLY by contract: the request carries the secret, the
 * response carries a masked hint and nothing else. There is no schema in this
 * package that can express a Bedrock secret in a response body, which is how
 * architecture §6 ("never returned by an API") is enforced rather than promised.
 */

/** `us.anthropic.*` inference profile ids — Bedrock is the only provider. */
export const BedrockModelIdSchema = z
  .string()
  .min(8)
  .max(200)
  .regex(/^[a-z]{2}\.[a-z0-9][a-z0-9.:-]*$/, 'expected a Bedrock model or inference-profile id');

export const AwsRegionSchema = z
  .string()
  .regex(/^[a-z]{2}(-[a-z]+)+-\d$/, 'expected an AWS region such as us-east-1');

export const AssistBudgetSchema = z.strictObject({
  /** Hard ceiling per invocation; a Bedrock outage or overrun degrades, never blocks. */
  maxOutputTokens: z.int().min(256).max(64_000),
  maxInvocationsPerDay: z.int().min(1).max(100_000),
  requestTimeoutMs: z.int().min(1_000).max(300_000),
});
export type AssistBudget = z.infer<typeof AssistBudgetSchema>;

export const AdminSettingsSchema = z.strictObject({
  organisationName: LabelSchema,
  supportContact: DescriptionSchema.optional(),
  /** Self-registration off means an admin invites every author. */
  selfRegistrationEnabled: z.boolean(),
  aiAssistEnabled: z.boolean(),
  defaultModelId: BedrockModelIdSchema,
  assistBudget: AssistBudgetSchema,
  attachmentMaxBytes: z.int().min(1_024).max(1_073_741_824),
  updatedAt: IsoDateTimeSchema,
  updatedBy: ShortTextSchema,
});
export type AdminSettings = z.infer<typeof AdminSettingsSchema>;

/** Masked hint only — never the key, never the secret. */
export const BedrockCredentialStatusSchema = z.strictObject({
  configured: z.boolean(),
  region: AwsRegionSchema.nullable(),
  /** e.g. `AKIA****************7QK4`. Derived server-side from the stored value. */
  accessKeyIdHint: z
    .string()
    .regex(/^[A-Z0-9]{4}\*{4,}[A-Z0-9]{4}$/)
    .nullable(),
  updatedAt: IsoDateTimeSchema.nullable(),
  lastTestedAt: IsoDateTimeSchema.nullable(),
  lastTestOk: z.boolean().nullable(),
});
export type BedrockCredentialStatus = z.infer<typeof BedrockCredentialStatusSchema>;

export const GetAdminSettingsRequestSchema = z.strictObject({});
export type GetAdminSettingsRequest = z.infer<typeof GetAdminSettingsRequestSchema>;
export const GetAdminSettingsResponseSchema = z.strictObject({
  settings: AdminSettingsSchema,
  bedrockCredential: BedrockCredentialStatusSchema,
});
export type GetAdminSettingsResponse = z.infer<typeof GetAdminSettingsResponseSchema>;

export const PutAdminSettingsRequestSchema = z.strictObject({
  organisationName: LabelSchema,
  supportContact: DescriptionSchema.optional(),
  selfRegistrationEnabled: z.boolean(),
  aiAssistEnabled: z.boolean(),
  defaultModelId: BedrockModelIdSchema,
  assistBudget: AssistBudgetSchema,
  attachmentMaxBytes: z.int().min(1_024).max(1_073_741_824),
});
export type PutAdminSettingsRequest = z.infer<typeof PutAdminSettingsRequestSchema>;
export const PutAdminSettingsResponseSchema = z.strictObject({ settings: AdminSettingsSchema });
export type PutAdminSettingsResponse = z.infer<typeof PutAdminSettingsResponseSchema>;

export const PutBedrockCredentialRequestSchema = z.strictObject({
  accessKeyId: z.string().regex(/^[A-Z0-9]{16,128}$/, 'expected an AWS access key id'),
  secretAccessKey: z.string().min(16).max(256),
  sessionToken: z.string().min(16).max(4_096).optional(),
  region: AwsRegionSchema,
});
export type PutBedrockCredentialRequest = z.infer<typeof PutBedrockCredentialRequestSchema>;
/** Response is the masked status — the write is acknowledged, never echoed. */
export const PutBedrockCredentialResponseSchema = z.strictObject({
  bedrockCredential: BedrockCredentialStatusSchema,
});
export type PutBedrockCredentialResponse = z.infer<typeof PutBedrockCredentialResponseSchema>;

export const TestConnectionRequestSchema = z.strictObject({
  /** Test a candidate model without changing the saved default. */
  modelId: BedrockModelIdSchema.optional(),
});
export type TestConnectionRequest = z.infer<typeof TestConnectionRequestSchema>;
export const TestConnectionResponseSchema = z.strictObject({
  ok: z.boolean(),
  checkedAt: IsoDateTimeSchema,
  latencyMs: z.int().min(0).nullable(),
  modelId: BedrockModelIdSchema,
  /** Provider diagnostic, already redacted server-side. */
  message: DescriptionSchema.nullable(),
});
export type TestConnectionResponse = z.infer<typeof TestConnectionResponseSchema>;

/* ── accounts and role assignments ──────────────────────────────────────────
 *
 * Who may do what, as data an administrator can edit, rather than as a role
 * baked in by whichever code path created the account.
 *
 * These are the only schemas in the platform that carry somebody else's email
 * address in a response, and the exception is deliberate: an administrator
 * granting `method:publish` has to know *which* person they are granting it to,
 * and the address is the only identifier w3id and this platform share. Every
 * route below is ADMIN-only in the manifest and re-checks `admin:manage` in its
 * use case. The rule the rest of the platform keeps — no principal directory
 * returns an address, a byline is not a reason to hand out a login — is
 * untouched: bylines still resolve through the authoring principal directory,
 * which answers with display names only.
 */

/** One account, as an administrator sees it. No credential material of any kind. */
export const AdminAccountSchema = z.strictObject({
  id: IdentifierSchema,
  email: EmailSchema,
  displayName: LabelSchema,
  role: ActorRoleSchema,
  status: UserAccountStatusSchema,
  /**
   * `true` when the account holds no password digest, so it can only arrive
   * through w3id. Derived from the absence of a hash — the hash itself is not on
   * the wire and has no schema that could carry it.
   */
  federated: z.boolean(),
  createdAt: IsoDateTimeSchema,
  lastLoginAt: IsoDateTimeSchema.nullable(),
});
export type AdminAccount = z.infer<typeof AdminAccountSchema>;

/**
 * A role decided for an address before that address has an account.
 *
 * w3id entitlement is birthright, so a first sign-in would otherwise land on
 * `OIDC_DEFAULT_ROLE` for everybody. An assignment is how an administrator says
 * "when this person arrives, they are a REVIEWER" without waiting for them to
 * sign in first and then hunting for them in the account list. It is applied and
 * marked `appliedAt` on the sign-in (or registration) that resolves the address,
 * which is also what makes it safe to keep: a spent assignment cannot silently
 * re-apply and undo a later change an administrator made by hand.
 */
export const RoleAssignmentSchema = z.strictObject({
  id: IdentifierSchema,
  email: EmailSchema,
  role: ActorRoleSchema,
  /** Why this grant exists — a ticket number, a team. Operator's note, not a claim. */
  note: ShortTextSchema.optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /** `null` until the address signs in. Set once, then never again. */
  appliedAt: IsoDateTimeSchema.nullable(),
  appliedToUserId: IdentifierSchema.nullable(),
});
export type RoleAssignment = z.infer<typeof RoleAssignmentSchema>;

export const ListAdminAccountsRequestSchema = z.strictObject({
  ...PageQuerySchema.shape,
  /** Substring match on address or display name, case-insensitive. */
  search: ShortTextSchema.optional(),
  role: ActorRoleSchema.optional(),
});
export type ListAdminAccountsRequest = z.infer<typeof ListAdminAccountsRequestSchema>;
export const ListAdminAccountsResponseSchema = z.strictObject({
  ...paginated(AdminAccountSchema).shape,
  /** How many accounts hold each role, over the whole table rather than this page. */
  roleTotals: z.record(ActorRoleSchema, z.int().min(0)),
});
export type ListAdminAccountsResponse = z.infer<typeof ListAdminAccountsResponseSchema>;

export const SetAccountRoleRequestSchema = z.strictObject({
  userId: IdentifierSchema,
  role: ActorRoleSchema,
});
export type SetAccountRoleRequest = z.infer<typeof SetAccountRoleRequestSchema>;
export const SetAccountRoleResponseSchema = z.strictObject({ account: AdminAccountSchema });
export type SetAccountRoleResponse = z.infer<typeof SetAccountRoleResponseSchema>;

export const ListRoleAssignmentsRequestSchema = z.strictObject({
  /** Spent assignments are kept as provenance; the list hides them unless asked. */
  includeApplied: z.stringbool().optional(),
});
export type ListRoleAssignmentsRequest = z.infer<typeof ListRoleAssignmentsRequestSchema>;
export const ListRoleAssignmentsResponseSchema = z.strictObject({
  assignments: z.array(RoleAssignmentSchema),
});
export type ListRoleAssignmentsResponse = z.infer<typeof ListRoleAssignmentsResponseSchema>;

/** Idempotent by address: saving twice changes the role, it does not queue a second grant. */
export const PutRoleAssignmentRequestSchema = z.strictObject({
  email: EmailSchema,
  role: ActorRoleSchema,
  note: ShortTextSchema.optional(),
});
export type PutRoleAssignmentRequest = z.infer<typeof PutRoleAssignmentRequestSchema>;
export const PutRoleAssignmentResponseSchema = z.strictObject({
  assignment: RoleAssignmentSchema,
  /**
   * Set when the address already had an account, in which case the role was applied
   * to it immediately rather than waiting for a sign-in that has already happened.
   */
  appliedTo: AdminAccountSchema.nullable(),
});
export type PutRoleAssignmentResponse = z.infer<typeof PutRoleAssignmentResponseSchema>;

export const DeleteRoleAssignmentRequestSchema = z.strictObject({
  assignmentId: IdentifierSchema,
});
export type DeleteRoleAssignmentRequest = z.infer<typeof DeleteRoleAssignmentRequestSchema>;
/**
 * Deleting an assignment withdraws a *pending* grant. It never demotes an account
 * that already applied one — that is a role change, and it has its own route.
 */
export const DeleteRoleAssignmentResponseSchema = z.strictObject({
  assignmentId: IdentifierSchema,
  deletedAt: IsoDateTimeSchema,
});
export type DeleteRoleAssignmentResponse = z.infer<typeof DeleteRoleAssignmentResponseSchema>;

/* ── the administrative purge ──────────────────────────────────────────────
   The one operation in the platform that destroys a published method.

   Everywhere else, `methodDeleteBlockers` in `schemas/methods.ts` is the whole
   rule and PUBLISHED is a refusal: readers and workshops may cite a published
   version, so it stays, and an author who wants to change it derives a variant.
   That is right for authors and wrong for the person who has to withdraw a
   method that was published by mistake, or that names something the
   organisation may no longer carry. Without this route the only remedy is
   psql on production, which is worse in every way: no capability check, no
   audit row, no defined order, and no chance for anyone to see what would go
   before it went.

   So the purge is deliberately not a relaxation of the blockers — it is a
   different operation with a different name, a different route prefix and one
   caller. What it does is stated in the plainest terms available, because a
   reader of this file must not be able to mistake its blast radius:

     IT DELETES THE VERSION, EVERY VARIANT DERIVED FROM IT (RECURSIVELY), EVERY
     PUBLISHED SNAPSHOT OF ALL OF THOSE, EVERY WORKSHOP SESSION RUN AGAINST ANY
     OF THEM — INCLUDING OTHER PEOPLE'S — AND EVERY PLAN GENERATED FROM ANY OF
     THEM, INCLUDING BASELINED ONES. IT IS IRREVERSIBLE AND LEAVES NO TOMBSTONE.
     THE AUDIT ROW IS THE ONLY SURVIVING RECORD.

   Which is why the shape below is a PREVIEW plus a CONFIRMATION rather than a
   single call. The preview is what makes the decision informed — it counts the
   sessions and plans that belong to other people before anyone types anything —
   and `confirmMethodId` is the second key: the ontology id the administrator
   read on the preview, sent back and re-checked server-side against the loaded
   version, so a stale row or a mis-copied surrogate id cannot purge a method
   nobody was looking at. The typed word stays in the client, as it does for the
   ordinary delete dialog: the server neither sees it nor could act on it.

   NO ADDRESSES. The counts say how many sessions and plans belong to someone
   other than the actor; they never say whose. A byline is not a reason to hand
   out a login, and a confirmation dialog is not a directory. */

/** What a purge would destroy, or — on the response — did. Same shape both ways. */
export const MethodPurgeCountsSchema = z.strictObject({
  /** The target and every variant reachable from it through `variantOf`. */
  methodVersions: z.int().min(1),
  publishedSnapshots: z.int().min(0),
  workshopSessions: z.int().min(0),
  plans: z.int().min(0),
  /** `Method` rows removed because the purge took their last remaining version. */
  methodFamilies: z.int().min(0),
  /**
   * Surviving sessions whose `publishedMethodVersionId` cited one of the destroyed
   * snapshots and had the citation cleared. Not a deletion — a session that adopted
   * a different version keeps running, it simply no longer points at a row that is gone.
   */
  clearedPublishedCitations: z.int().min(0),
});
export type MethodPurgeCounts = z.infer<typeof MethodPurgeCountsSchema>;

/** One version the purge would take, named well enough to be recognised on sight. */
export const MethodPurgeVersionSchema = z.strictObject({
  id: IdentifierSchema,
  methodId: MethodIdSchema,
  name: LabelSchema,
  versionLabel: ShortTextSchema,
  state: MethodStateSchema,
  /** `0` is the version the purge was aimed at; deeper rows are variants of variants. */
  depth: z.int().min(0),
  publishedAt: IsoDateTimeSchema.nullable(),
  workshopSessionCount: z.int().min(0),
  planCount: z.int().min(0),
});
export type MethodPurgeVersion = z.infer<typeof MethodPurgeVersionSchema>;

export const MethodPurgeImpactSchema = z.strictObject({
  /** Depth-first from the target, so a reader sees lineage rather than a set. */
  versions: z.array(MethodPurgeVersionSchema).min(1),
  counts: MethodPurgeCountsSchema,
  /**
   * How many of those sessions and plans were created by someone other than the
   * administrator running the purge. The number that makes this decision serious.
   */
  workshopSessionsOfOthers: z.int().min(0),
  plansOfOthers: z.int().min(0),
  /** At least one plan has been baselined: a schedule somebody is working to. */
  hasBaselinedPlans: z.boolean(),
});
export type MethodPurgeImpact = z.infer<typeof MethodPurgeImpactSchema>;

export const PreviewMethodPurgeRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
});
export type PreviewMethodPurgeRequest = z.infer<typeof PreviewMethodPurgeRequestSchema>;
export const PreviewMethodPurgeResponseSchema = z.strictObject({
  impact: MethodPurgeImpactSchema,
});
export type PreviewMethodPurgeResponse = z.infer<typeof PreviewMethodPurgeResponseSchema>;

export const PurgeMethodRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  /** The `methodId` shown on the preview, re-checked against the loaded version. */
  confirmMethodId: MethodIdSchema,
});
export type PurgeMethodRequest = z.infer<typeof PurgeMethodRequestSchema>;
export const PurgeMethodResponseSchema = z.strictObject({
  /** Echoed so the client can tell the operator what it withdrew, once. */
  methodId: MethodIdSchema,
  name: LabelSchema,
  removed: MethodPurgeCountsSchema,
  purgedAt: IsoDateTimeSchema,
});
export type PurgeMethodResponse = z.infer<typeof PurgeMethodResponseSchema>;
